<template>
  <focus-trap :active="form" :escapeDeactivates="false" :initialFocus="() => getInitialFocus()">
    <b-form class="form-horizontal well" v-show="form">
      <div class="form-group" v-if="showTitle">
        <div class="col-sm-8 col-sm-offset-2">
          <h2 style="font-size: 2rem">Compose Message</h2>
        </div>
      </div>
      <div class="form-group" v-if="bulkUserMode">
        <div class="col-sm-8">
          To: <b>{{ toUserString }}</b>
        </div>
      </div>
      <div v-else>
        <label for="recipient" class="col-sm-2 control-label">To</label>
        <div>
          <userSearch
            :limitText="limitText"
            :userSearch="userSearch"
            :users="users"
            :isLoading="isLoading"
            :twoCharacterMessage="twoCharacterMessage"
            :emptySetMessage="emptySetMessage"
            :noResultMessage="noResultMessage"
            :usersSelected="usersSelected"
            :defaultSelectedUsers="defaultSelectedUsers"
          >
          </userSearch>
        </div>
      </div>
      <div class="form-group">
        <label for="subject" class="sr-only">Email Subject</label>
        <div class="col-md-8">
          <b-form-input
            type="text"
            class="form-control message-subject"
            id="subject"
            v-model="composeEmail.subject"
            name="subject"
            aria-label="Email Subject"
          ></b-form-input>
        </div>
      </div>
      <div class="form-group">
        <label id="messageBodyLabel">Message Body</label>
        <div class="col-md-8">
          <quill-editor
            ref="messageBody"
            v-model="content"
            :options="editorOption"
            @ready="onEditorReady"
            aria-labelledby="messageBodyLabel"
          />
        </div>
      </div>
      <div class="form-group">
        <div class="col-sm-8 col-sm-offset-2">
          <b-button @click="compose" variant="primary" id="send">Send</b-button>
          <b-button @click="exitForm" variant="primary" id="cancel" class="ml-3">Cancel</b-button>
        </div>
      </div>
    </b-form>
  </focus-trap>
</template>
<script>
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import userSearch from '../messaging/message-user-search.vue';
import { FocusTrap } from 'focus-trap-vue';
import { quillEditor } from 'vue-quill-editor';

export default {
  name: 'MessageCompose',

  components: {
    FocusTrap,
    userSearch,
    quillEditor
  },

  data() {
    return {
      content: this.composeEmail.message,
      editorOption: {
        modules: {
          toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']]
        },
        placeholder: 'Type here...',
        theme: 'snow'
      }
    };
  },

  methods: {
    onEditorReady(quill) {
      const fixAria = (selector, label) => {
        const el = document.querySelector(selector);

        if (el && el.getAttribute('aria-label') !== label) {
          el.setAttribute('aria-label', label);
          el.setAttribute('title', label);

          if (el.tagName !== 'BUTTON') {
            el.setAttribute('role', 'button');
          }
        }
      };

      const fixLinks = () => {
        document.querySelectorAll('.ql-editor a, .ql-preview').forEach((link) => {
          if (!link.textContent.trim()) {
            link.setAttribute('aria-label', 'Link');

            if (!link.querySelector('.sr-only')) {
              const span = document.createElement('span');
              span.className = 'sr-only';
              span.textContent = 'Link';
              link.appendChild(span);
            }
          }
        });
      };

      const applyEditorAccessibility = () => {
        document.querySelectorAll('[contenteditable="true"]').forEach((el, index) => {
          if (!el.id) {
            el.id = `editor-${index}`;
          }

          let label = document.querySelector(`label[for="${el.id}"]`);

          if (!label) {
            label = document.createElement('label');
            label.setAttribute('for', el.id);
            label.className = 'sr-only';
            label.innerText = 'Message Body';

            el.parentNode.insertBefore(label, el);
          }

          el.setAttribute('role', 'textbox');
          el.setAttribute('aria-multiline', 'true');
        });
      };

      const applyQuillEditorFix = () => {
        document.querySelectorAll('.ql-editor').forEach((editor) => {
          if (!editor.getAttribute('aria-label')) {
            editor.setAttribute('aria-label', 'Message Body');
            editor.setAttribute('role', 'textbox');
            editor.setAttribute('aria-multiline', 'true');
          }
        });
      };

      const applyTooltipFix = () => {
        document.querySelectorAll('.ql-tooltip input').forEach((input) => {
          if (!input.getAttribute('aria-label')) {
            input.setAttribute('aria-label', 'Enter link URL');
          }
        });
      };

      const toolbarLabels = {
        '.ql-bold': 'Bold',
        '.ql-italic': 'Italic',
        '.ql-underline': 'Underline',
        '.ql-link': 'Insert Link',
        '.ql-list[value="ordered"]': 'Ordered List',
        '.ql-list[value="bullet"]': 'Bullet List'
      };

      Object.entries(toolbarLabels).forEach(([selector, text]) => {
        fixAria(selector, text);
      });

      const editorEl = quill.root;

      if (editorEl) {
        editorEl.setAttribute('id', 'messageBodyEditor');
        editorEl.setAttribute('aria-labelledby', 'messageBodyLabel');
        editorEl.setAttribute('role', 'textbox');
        editorEl.setAttribute('aria-multiline', 'true');
      }

      // Initial execution
      setTimeout(() => {
        applyEditorAccessibility();
        applyQuillEditorFix();
        fixLinks();
        applyTooltipFix();
      }, 300);

      const observer = new MutationObserver(() => {
        applyEditorAccessibility();
        applyQuillEditorFix();
        applyTooltipFix();
        fixLinks();

        fixAria('.ql-action', 'Save Link');
        fixAria('.ql-remove', 'Remove Link');
      });

      if (this.$refs.messageBody && this.$refs.messageBody.$el) {
        observer.observe(this.$refs.messageBody.$el, {
          childList: true,
          subtree: true
        });
      }
    },

    onEditorChange(editor) {
      this.$emit('input', editor.html);
      this.composeEmail.message = this.content;
    },

    getInitialFocus() {
      let element;

      if (
        this.$parent.form === true &&
        this.$children &&
        this.$children[0] &&
        this.$children[0].$children &&
        this.$children[0].$children[0] &&
        this.$children[0].$children[0].$refs &&
        this.$children[0].$children[0].$refs.recipient &&
        this.$children[0].$children[0].$refs.recipient.$el
      ) {
        element = this.$children[0].$children[0].$refs.recipient.$el;
      } else if (
        this.replyForm &&
        this.$refs.messageBody &&
        this.$refs.messageBody.$el.children &&
        this.$refs.messageBody.$el.children[1] &&
        this.$refs.messageBody.$el.children[1].firstChild
      ) {
        element = this.$refs.messageBody.$el.children[1].firstChild;
      } else {
        element = this.$refs.messageBody.$el;
      }

      return element;
    }
  },

  props: [
    'form',
    'inputForm',
    'exitForm',
    'composeEmail',
    'compose',
    'users',
    'isLoading',
    'twoCharacterMessage',
    'emptySetMessage',
    'noResultMessage',
    'limitText',
    'userSearch',
    'usersSelected',
    'defaultSelectedUsers',
    'replyForm',
    'showTitle',
    'bulkUserMode',
    'toUserString'
  ]
};
</script>
<style>
.wfr-quill {
  min-height: 200px;
}
.ql-container {
  min-height: 200px;
}
</style>
